import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BOX_KG } from '../../utils/packaging';

interface LineItem {
  productId: string;
  description: string;
  grade: string;
  numberOfBags: string;
  bagWeightKg: string;
  netWeightKg: string;
  shippingMark: string;
}

interface Buyer { id: string; companyName: string; }
interface Offer { id: string; offerNumber: string; buyerId: string; buyerName?: string; }
interface Coffee { id: string; title: string; gradeBreakdown: Record<string, number>; }

const emptyLineItem: LineItem = { productId: '', description: '', grade: '', numberOfBags: '', bagWeightKg: '60', netWeightKg: '', shippingMark: '' };

export const PackingListForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    buyerId: '', offerId: '', shippingMark: '', incoterms: 'FOB Mombasa', shippingMonth: '', notes: '',
  });
  const [lineItems, setLineItems] = useState<LineItem[]>([{ ...emptyLineItem }]);
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [coffees, setCoffees] = useState<Coffee[]>([]);
  const [status, setStatus] = useState('draft');
  const [saving, setSaving] = useState(false);
  const [loadingOffer, setLoadingOffer] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/admin/buyers', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setBuyers(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch('/api/admin/offers', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setOffers(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch('/api/coffees?includeArchived=true', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setCoffees(Array.isArray(data) ? data : []))
      .catch(() => {});

    if (isEdit) {
      fetch(`/api/admin/packing-lists?id=${id}`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          setForm({
            buyerId: data.buyerId || '',
            offerId: data.offerId || '',
            shippingMark: data.shippingMark || '',
            incoterms: data.incoterms || 'FOB Mombasa',
            shippingMonth: data.shippingMonth || '',
            notes: data.notes || '',
          });
          setStatus(data.status || 'draft');
          if (data.lineItems?.length) {
            setLineItems(data.lineItems.map((li: any) => ({
              productId: li.productId || '',
              description: li.description,
              grade: li.grade || '',
              numberOfBags: String(li.numberOfBags ?? ''),
              bagWeightKg: String(li.bagWeightKg ?? '60'),
              netWeightKg: String(li.netWeightKg ?? ''),
              shippingMark: li.shippingMark || '',
            })));
          }
        });
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const loadFromOffer = async (offerId: string) => {
    setForm(prev => ({ ...prev, offerId }));
    if (!offerId) return;
    setLoadingOffer(true);
    try {
      const res = await fetch(`/api/admin/packing-lists?action=from-offer&offerId=${offerId}`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        setForm(prev => ({
          ...prev,
          offerId,
          buyerId: prev.buyerId || data.buyerId || '',
          incoterms: data.incoterms || prev.incoterms,
          shippingMonth: data.shippingMonth || prev.shippingMonth,
        }));
        if (data.lineItems?.length) setLineItems(data.lineItems);
      }
    } catch {
      // leave lines as-is on failure
    } finally {
      setLoadingOffer(false);
    }
  };

  const updateLineItem = (i: number, field: keyof LineItem, value: string) => {
    setLineItems(prev => {
      const next = [...prev];
      const updated = { ...next[i], [field]: value };
      // Bags/weight edits re-derive net weight as a convenience for hand-entered rows.
      // Net weight itself is otherwise left alone — e.g. an exact kg total pulled from
      // an offer — so it's never silently overwritten by a rounded bag estimate.
      if (field === 'numberOfBags' || field === 'bagWeightKg') {
        const bags = parseInt(updated.numberOfBags) || 0;
        const bagWeightKg = parseFloat(updated.bagWeightKg) || 0;
        updated.netWeightKg = (bags * bagWeightKg).toFixed(2);
      }
      next[i] = updated;
      return next;
    });
  };

  const selectProductForLine = (i: number, productId: string) => {
    const coffee = coffees.find(c => c.id === productId);
    setLineItems(prev => {
      const next = [...prev];
      next[i] = { ...next[i], productId, description: coffee?.title || next[i].description, grade: '' };
      return next;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...emptyLineItem }]);
  const removeLine = (i: number) => setLineItems(prev => prev.filter((_, j) => j !== i));

  const netWeight = (li: LineItem) => parseFloat(li.netWeightKg) || 0;
  const totalBags = lineItems.reduce((sum, li) => sum + (parseInt(li.numberOfBags) || 0), 0);
  const totalNetWeightKg = lineItems.reduce((sum, li) => sum + netWeight(li), 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const validItems = lineItems
      .filter(li => li.description && li.numberOfBags)
      .map(li => ({
        ...li,
        netWeightKg: li.netWeightKg !== '' ? li.netWeightKg : ((parseInt(li.numberOfBags) || 0) * (parseFloat(li.bagWeightKg) || 0)).toFixed(2),
      }));
    if (validItems.length === 0) {
      setError('Add at least one line item with a description and bag count.');
      setSaving(false);
      return;
    }

    const body = isEdit ? { id, ...form, lineItems: validItems } : { ...form, lineItems: validItems };

    try {
      const res = await fetch('/api/admin/packing-lists', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to save');
      navigate('/admin/packing-lists');
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 border border-[#E8E6E2] rounded-xl bg-[#FAFAF8] text-[#1A1A1A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all placeholder-[#C4C4C4]";
  const labelClass = "block text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold mb-1.5";

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{isEdit ? 'Edit Packing List' : 'New Packing List'}</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">Bags &amp; weights for staff to prepare shipment &mdash; no pricing</p>
        </div>
        <button onClick={() => navigate('/admin/packing-lists')} className="text-sm text-[#A3A3A3] hover:text-[#1A1A1A] transition-colors font-medium">
          Back to packing lists
        </button>
      </div>

      {isEdit && status === 'sent' && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-100 rounded-xl text-amber-700 text-sm font-medium">
          This packing list has already been sent. Update it below and save, then use "Resend" from the list to notify staff of the changes &mdash; saving here alone won't notify them.
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Shipment Details</h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className={labelClass}>Buyer</label>
              <select name="buyerId" value={form.buyerId} onChange={handleChange} className={inputClass}>
                <option value="">Select buyer...</option>
                {buyers.map(b => <option key={b.id} value={b.id}>{b.companyName}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Load Lines From Offer (optional)</label>
              <select value={form.offerId} onChange={e => loadFromOffer(e.target.value)} disabled={loadingOffer} className={inputClass}>
                <option value="">None — build manually</option>
                {offers.map(o => <option key={o.id} value={o.id}>{o.offerNumber}{o.buyerName ? ` — ${o.buyerName}` : ''}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Shipping Mark</label>
              <input name="shippingMark" value={form.shippingMark} onChange={handleChange} placeholder="e.g. L&Y" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Incoterms</label>
              <select name="incoterms" value={form.incoterms} onChange={handleChange} className={inputClass}>
                <option value="FOB Mombasa">FOB Mombasa</option>
                <option value="CIF">CIF</option>
                <option value="CFR">CFR</option>
                <option value="EXW">EXW</option>
                <option value="FCA">FCA</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Shipping Month</label>
              <input name="shippingMonth" value={form.shippingMonth} onChange={handleChange} placeholder="e.g. September 2026" className={inputClass} />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Lines</h2>

          <div className="flex gap-3 items-end mb-2 px-1">
            <div className="flex-1"><span className={labelClass}>Lot (optional)</span></div>
            <div className="flex-1"><span className={labelClass}>Description</span></div>
            <div className="w-20"><span className={labelClass}>Grade</span></div>
            <div className="w-24"><span className={labelClass}>Bags</span></div>
            <div className="w-24"><span className={labelClass}>Bag Wt (kg)</span></div>
            <div className="w-28"><span className={labelClass}>Mark</span></div>
            <div className="w-28"><span className={labelClass}>Net Wt (kg)</span></div>
            <div className="w-8" />
          </div>

          <div className="space-y-2">
            {lineItems.map((li, i) => {
              const coffee = coffees.find(c => c.id === li.productId);
              const grades = coffee ? Object.keys(coffee.gradeBreakdown || {}) : [];
              return (
                <div key={i} className="flex gap-3 items-center p-3 bg-[#FAFAF8] rounded-xl border border-[#E8E6E2]/40">
                  <div className="flex-1">
                    <select value={li.productId} onChange={e => selectProductForLine(i, e.target.value)} className={inputClass}>
                      <option value="">— generic —</option>
                      {coffees.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                  </div>
                  <div className="flex-1">
                    <input value={li.description} onChange={e => updateLineItem(i, 'description', e.target.value)} placeholder="e.g. Classic Peaberry" className={inputClass} />
                  </div>
                  <div className="w-20">
                    {grades.length > 0 ? (
                      <select value={li.grade} onChange={e => updateLineItem(i, 'grade', e.target.value)} className={inputClass}>
                        <option value="">-</option>
                        {grades.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    ) : (
                      <input value={li.grade} onChange={e => updateLineItem(i, 'grade', e.target.value)} placeholder="PB" className={inputClass} />
                    )}
                  </div>
                  <div className="w-24">
                    <input type="number" value={li.numberOfBags} onChange={e => updateLineItem(i, 'numberOfBags', e.target.value)} placeholder="0" className={inputClass} />
                  </div>
                  <div className="w-24">
                    <input type="number" step="0.01" value={li.bagWeightKg} onChange={e => updateLineItem(i, 'bagWeightKg', e.target.value)} placeholder="60" className={inputClass} />
                  </div>
                  <div className="w-28">
                    <input value={li.shippingMark} onChange={e => updateLineItem(i, 'shippingMark', e.target.value)} placeholder={form.shippingMark || 'default'} className={inputClass} />
                  </div>
                  <div className="w-28">
                    <input
                      type="number"
                      step="0.01"
                      value={li.netWeightKg}
                      onChange={e => updateLineItem(i, 'netWeightKg', e.target.value)}
                      placeholder={((parseInt(li.numberOfBags) || 0) * (parseFloat(li.bagWeightKg) || 0)).toFixed(2)}
                      className={inputClass}
                    />
                  </div>
                  <button type="button" onClick={() => removeLine(i)} className="w-8 h-8 flex items-center justify-center text-[#C4C4C4] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" disabled={lineItems.length === 1}>
                    &times;
                  </button>
                </div>
              );
            })}
          </div>

          <button type="button" onClick={addLine} className="mt-4 px-4 py-2.5 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] border border-dashed border-[#E8E6E2] hover:border-[#A3A3A3] rounded-xl transition-all w-full">
            + Add line item
          </button>
          <p className="mt-2 text-[11px] text-[#A3A3A3]">"Load Lines From Offer" keeps each lot's exact kg total and estimates a box count at {BOX_KG}kg/box (this harvest's standard pack) — net weight is never derived from that estimate, so it stays accurate even if the box count is off.</p>

          <div className="mt-6 pt-5 border-t border-[#E8E6E2]/60 flex justify-end">
            <div className="w-72 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#A3A3A3]">Total Bags</span>
                <span className="text-[#1A1A1A] tabular-nums font-medium">{totalBags.toLocaleString('en-US')}</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-3 border-t-2 border-[#1A1A1A]">
                <span className="text-[#1A1A1A]">Total Net Weight</span>
                <span className="text-[#1A1A1A] tabular-nums">{totalNetWeightKg.toLocaleString('en-US')} kg</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Notes</h2>
          <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Anything staff should know when preparing this shipment..." className={inputClass} />
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={saving} className="px-8 py-3 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all disabled:opacity-40 shadow-sm shadow-black/10">
            {saving ? 'Saving...' : isEdit ? 'Update Packing List' : 'Save Draft'}
          </button>
          <button type="button" onClick={() => navigate('/admin/packing-lists')} className="px-6 py-3 border border-[#E8E6E2] rounded-xl text-sm text-[#6B6B6B] hover:bg-[#F5F3F0] hover:border-[#E0DDD8] transition-all font-medium">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
